import { formatCurrency } from "@/lib/utils";
import type { CheckoutLine, ResolvedLine } from "@/lib/express-cart/checkout";
import type { CheckoutProduct } from "@/lib/express-cart/variants";
import { QuantityStepper } from "./QuantityStepper";
import { VariantPicker } from "./VariantPicker";

export interface GroupRow {
  line: CheckoutLine;
  resolved: ResolvedLine;
  removable: boolean;
}

interface Props {
  product: CheckoutProduct;
  rows: GroupRow[];
  dimInactive?: boolean;
  canAdd: boolean;
  notice: string | null;
  highlightLineKey: string | null;
  onSelect: (clientKey: string, axisName: string, value: string) => void;
  onQuantityChange: (clientKey: string, quantity: number) => void;
  onRemove: (clientKey: string) => void;
  onAdd: () => void;
  stockBySku?: Map<string, number>;
  priceBySku?: Map<string, number>;
  saveFailureByRow?: Map<string, string>;
  showStockMessages?: boolean;
}

function variantLabel(resolved: ResolvedLine): string {
  return resolved.variant?.title ?? resolved.product.title;
}

export function ProductGroup({
  product,
  rows,
  dimInactive = true,
  canAdd,
  notice,
  highlightLineKey,
  onSelect,
  onQuantityChange,
  onRemove,
  onAdd,
  stockBySku,
  priceBySku,
  saveFailureByRow,
  showStockMessages = true,
}: Props) {
  const firstImage = rows.find((r) => r.resolved.variant?.image)?.resolved.variant?.image;
  const imageSrc = firstImage ?? product.image_url;
  const resolvedRows = rows.map((row) => {
    const sku = row.resolved.variant?.sku ?? row.resolved.product.sku;
    const key = sku ? `${product.id}:${sku}` : undefined;
    const liveStock = key ? stockBySku?.get(key) : undefined;
    const livePrice = key ? priceBySku?.get(key) : undefined;
    const maxQuantity = liveStock === undefined
      ? row.resolved.maxQuantity
      : Math.min(row.resolved.maxQuantity, liveStock);
    const unitPriceCents = livePrice ?? row.resolved.unitPriceCents;
    return {
      ...row,
      resolved: {
        ...row.resolved,
        maxQuantity,
        unitPriceCents,
        lineTotalCents: unitPriceCents * row.line.quantity,
        inStock: row.resolved.inactive || row.resolved.needsVariant || row.line.quantity <= maxQuantity,
      },
      basePriceCents: row.resolved.unitPriceCents,
      liveStock,
      livePrice,
    };
  });
  const groupTotal = resolvedRows.reduce((sum, r) => sum + r.resolved.lineTotalCents, 0);
  const allInactive = rows.every((r) => r.resolved.inactive);
  const hasVariants = product.variants.length > 0;

  return (
    <li className="py-5">
      <div className="flex gap-4">
        <div
          aria-hidden
          className={`flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-(--color-primary-light) text-xl font-bold text-(--color-primary) ${dimInactive && allInactive ? "opacity-40" : ""}`}
        >
          {imageSrc ? (
            <img src={imageSrc} alt="" className="h-full w-full object-cover" />
          ) : (
            product.title.charAt(0)
          )}
        </div>
        <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
          <div>
            <p className={`font-medium leading-snug ${dimInactive && allInactive ? "text-(--color-text-placeholder)" : ""}`}>{product.title}</p>
            {!hasVariants && (
              <p className="text-sm text-(--color-text-secondary) tabular-nums">{formatCurrency(product.price_cents)} each</p>
            )}
          </div>
          <p className={`shrink-0 font-medium tabular-nums ${dimInactive && allInactive ? "text-(--color-text-placeholder)" : ""}`}>
            {formatCurrency(groupTotal)}
          </p>
        </div>
      </div>

      <div className="mt-3 space-y-3 sm:pl-20">
        {resolvedRows.map(({ line, resolved, removable, liveStock, livePrice, basePriceCents }) => {
          const failureKey = `${product.id}|${resolved.variant?.sku ?? ""}`;
          const saveFailure = saveFailureByRow?.get(failureKey);
          return (
          <div
            key={line.clientKey}
            className={[
              "rounded-xl border p-3 transition-colors",
              dimInactive && resolved.inactive ? "border-dashed border-(--color-surface-border) bg-(--color-surface-secondary)" : "border-(--color-surface-border)",
              highlightLineKey === line.clientKey ? "border-(--color-primary) ring-2 ring-(--color-primary)/20" : "",
            ].join(" ")}
          >
            {hasVariants && (
              <div className={`mb-3 ${dimInactive && resolved.inactive ? "opacity-50" : ""}`}>
                <VariantPicker product={product} selection={line.selection} onSelect={(axis, value) => onSelect(line.clientKey, axis, value)} />
              </div>
            )}

            {!resolved.inactive && resolved.needsVariant && (
              <p className="mb-3 text-xs font-medium text-(--color-primary)">Choose your options to continue</p>
            )}
            {saveFailure && (
              <p role="alert" className="mb-3 text-xs font-medium text-(--color-error)">
                Adjust the quantity or remove this item to continue.
              </p>
            )}
            {showStockMessages && !resolved.inStock && <p className="mb-3 text-xs font-medium text-(--color-error)">Out of stock</p>}
            {showStockMessages && liveStock !== undefined && line.quantity > liveStock && resolved.inStock && (
              <p className="mb-3 text-xs font-medium text-(--color-error)">
                Only {liveStock} available from the retailer.
              </p>
            )}
            {livePrice !== undefined && livePrice !== basePriceCents && (
              <p className="mb-3 text-xs font-medium text-(--color-warning)">
                Current retailer price: {formatCurrency(livePrice)} each.
              </p>
            )}
            {showStockMessages && !resolved.inactive && resolved.variant && resolved.maxQuantity > 0 && resolved.maxQuantity <= 3 && (
              <p className="mb-3 text-xs text-(--color-warning)">Only {resolved.maxQuantity} left</p>
            )}
            {resolved.inactive && (
              <p className="mb-3 text-xs text-(--color-text-muted)">
                {(showStockMessages && resolved.maxQuantity === 0 && !resolved.needsVariant) || resolved.maxQuantity == 0
                  ? "Out of stock."
                  : "Not in your order. Tap + to add it back."}
              </p>
            )}

            <div className="flex items-center justify-between gap-3">
              <QuantityStepper
                label={variantLabel(resolved)}
                quantity={line.quantity}
                max={resolved.maxQuantity}
                onChange={(q) => onQuantityChange(line.clientKey, q)}
              />
              <div className="flex items-center gap-3">
                {resolved.variant && !resolved.inactive && (
                  <span className="text-sm text-(--color-text-secondary) tabular-nums">
                    {formatCurrency(resolved.unitPriceCents)} each
                  </span>
                )}
                {removable && (
                  <button
                    type="button"
                    onClick={() => onRemove(line.clientKey)}
                    aria-label={`Remove ${variantLabel(resolved)} row`}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-(--color-text-muted) hover:bg-(--color-bg-hover)"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>
          );
        })}

        {notice && (
          <p role="status" className="text-xs font-medium text-(--color-primary)">
            {notice}
          </p>
        )}

        {canAdd && (
          <button type="button" onClick={onAdd} className="text-sm font-medium text-(--color-primary) hover:underline">
            + Add another size or colour
          </button>
        )}
      </div>
    </li>
  );
}
